import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { PaymentMethod, PaymentStatusBody } from "../api";
import { fmtMoney } from "../api";
import { Button } from "./ui/button";
import { Dialog } from "./ui/dialog";
import { MoneyInput } from "./money-input";
import { PaymentMethodSelect } from "./payment-method-select";

/**
 * Saqlangan xarid/sotuv/ish qarzini so'ndirish (to'lov qabul qilish) dialogi.
 * Jami summa va joriy qarzdorlik ko'rsatiladi; egasi faqat "to'lanayotgan
 * summa"ni kiritadi (default = qarzdorlik). Qoldiq to'liq yopilsa "tolandi",
 * aks holda "qisman" holatini backendga yuboramiz (absolyut tolangan_summa).
 * Faqat qarzdorlik mavjud (qarz > 0) hujjatlar uchun ochiladi.
 *
 * Bu dialog uchta hujjat turi uchun umumiy (ish / sotuv / xarid), ya'ni "mijoz
 * qarzini so'ndirdi" ham, "biz yetkazib beruvchi xaridini to'ladik" ham shu
 * yerdan o'tadi — to'lov usuli tanlovi ham shuning uchun bitta joyda.
 */
export function PaymentEditDialog({
  open,
  onOpenChange,
  jami,
  initialPaid,
  partyLabel,
  onSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jami: number;
  initialPaid: number;
  partyLabel?: string;
  onSave: (body: PaymentStatusBody) => Promise<void>;
}) {
  const { t } = useTranslation(["ombor", "common"]);
  const qarz = Math.max(jami - initialPaid, 0);
  // Default — joriy qarzdorlik summasi (to'liq so'ndirish).
  const [amount, setAmount] = useState(String(Math.round(qarz)));
  // Ixtiyoriy: tanlanmasa hujjatning mavjud usuli o'zgarmaydi.
  const [usul, setUsul] = useState<PaymentMethod | "">("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    setErr(null);
    const n = Number(amount) || 0;
    if (n <= 0 || n > qarz) {
      setErr(t("shared.paymentEdit.rangeError"));
      return;
    }
    const newPaid = initialPaid + n;
    const body: PaymentStatusBody =
      newPaid >= jami
        ? { tolov_holati: "tolandi" }
        : { tolov_holati: "qisman", tolangan_summa: String(newPaid) };
    if (usul) body.tolov_usuli = usul;
    setBusy(true);
    try {
      await onSave(body);
      toast.success(t("shared.paymentEdit.saved"));
      onOpenChange(false);
    } catch (e) {
      setErr((e as { message?: string }).message || t("common:common.error"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)} contentClassName="max-w-sm">
      <div className="space-y-4 p-5">
        <div>
          <h2 className="pr-8 text-lg font-semibold leading-none tracking-tight">
            {t("shared.paymentEdit.title")}
          </h2>
          {partyLabel && (
            <p className="mt-1.5 text-sm text-muted-foreground">{partyLabel}</p>
          )}
        </div>

        <div className="space-y-2 rounded-md border bg-muted/30 px-3 py-2.5">
          <div className="flex items-baseline justify-between">
            <span className="text-xs uppercase tracking-wider text-muted-foreground">
              {t("shared.paymentEdit.jami")}
            </span>
            <span className="font-medium tabular-nums">{fmtMoney(jami)}</span>
          </div>
          <div className="flex items-baseline justify-between">
            <span className="text-xs uppercase tracking-wider text-muted-foreground">
              {t("shared.paymentEdit.qarz")}
            </span>
            <span className="text-lg font-bold tabular-nums text-destructive">{fmtMoney(qarz)}</span>
          </div>
        </div>

        <div className="space-y-1">
          <label className="text-xs uppercase tracking-wider text-muted-foreground">
            {t("shared.paymentEdit.tolanayotgan")}
          </label>
          <MoneyInput
            value={amount}
            onChange={setAmount}
            disabled={busy}
            autoFocus
            className="h-9 text-right tabular-nums"
            placeholder="0"
          />
        </div>

        <PaymentMethodSelect
          id="pay-usul"
          value={usul}
          onChange={setUsul}
          disabled={busy}
          className="space-y-1"
        />

        {err && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {err}
          </div>
        )}

        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            className="flex-none"
          >
            {t("sotuvlar.cancel")}
          </Button>
          <Button type="button" onClick={submit} disabled={busy} className="flex-1">
            {busy ? t("sotuvlar.form.submitting") : t("shared.paymentEdit.save")}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
